import classNames from 'classnames';
import dayjs from 'dayjs';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCorrespondenceDocumentFileUrl } from '@/modules/issuer-dashboard/helpers/getCorrespondenceDocumentFileUrl.ts';
import { getCorrespondenceDocumentFilename } from '@/modules/issuer-dashboard/helpers/getCorrespondenceDocumentFilename.ts';
import { useIssuerCommunicationMessagesQuery } from '@/modules/issuer-dashboard/queries/communications.ts';
import { Badge, ModalContentWrapper, PopoverMenu, SelectButtonsNew, Spinner } from 'ui';
import { AIBeautifyIcon, ArrowShortLeftIcon, ClipIcon, FilePlusIcon, TriangleIcon } from 'ui/icons';
import { sanitizeCorrespondenceHtml } from 'ui/utils';
import {
  CommunicationType,
  CorrespondenceCommunicationActivityItem,
  CorrespondenceDocuments,
} from 'api';

enum MessageViewMode {
  AIExtracted = 'ai_extracted',
  Original = 'original',
}

const viewModeOptions = [
  {
    value: MessageViewMode.AIExtracted,
    label: (
      <>
        <img src={AIBeautifyIcon} alt="icon" width={12} /> <span> AI Extracted</span>
      </>
    ),
  },
  {
    value: MessageViewMode.Original,
    label: (
      <>
        <img src={FilePlusIcon} alt="icon" width={12} /> <span>Original</span>
      </>
    ),
  },
];

interface CorrespondenceMessageProps {
  documents?: CorrespondenceDocuments[];
  fromName: ReactNode;
  fromEmail: ReactNode;
  to?: string;
  cc?: string;
  date?: string;
  subject: string;
  content?: string;
  ai_extracted?: string;
}

const CorrespondenceMessage = ({
  to,
  cc,
  date,
  documents,
  subject,
  content,
  fromName,
  fromEmail,
  ai_extracted,
}: CorrespondenceMessageProps) => {
  const [mode, setMode] = useState(
    ai_extracted ? MessageViewMode.AIExtracted : MessageViewMode.Original,
  );
  const [isDocumentsListOpen, setIsDocumentsListOpen] = useState(false);
  const sanitizedContent = useMemo(() => sanitizeCorrespondenceHtml(content) || '---', [content]);

  const handleToggleOpenReportsList = useCallback(() => {
    setIsDocumentsListOpen(open => !open);
  }, []);

  const extractInfoItems = useMemo(
    () => [
      [
        'from:',
        <>
          {fromName} {fromEmail}
        </>,
      ],
      ['to:', to || '---'],
      ['cc:', cc || '---'],
      ['date:', date ? dayjs(date).format('ddd, MMM DD, h:mmA') : '---'],
      ['subject:', subject],
    ],
    [cc, date, fromEmail, fromName, subject, to],
  );

  return (
    <div className="pb-3">
      <div className="flex justify-between items-center">
        <div className="flex gap-1 items-center flex-wrap">
          <b>{fromName}</b>
          <div className="text-sm text-gray-500 truncate max-w-[300px]">{fromEmail}</div>
        </div>
        <div className="text-xs text-gray-500 shrink-0 self-baseline">
          {date ? dayjs(date).format('ddd, MMM DD, h:mmA') : '---'}
        </div>
      </div>
      <PopoverMenu
        button={
          <div className="flex gap-1 text-sm text-gray-500 mt-1">
            <div className="max-w-52 truncate">to {to || '---'}</div>
            <img alt="details" src={TriangleIcon} />
          </div>
        }
        headerMenu={
          <div className="relative z-20 p-4 rounded shadow space-y-1 text-sm">
            {extractInfoItems.map(([label, value], index) => (
              <div className="flex gap-4" key={index}>
                <div className="w-16 text-gray-500 text-end shrink-0">{label}</div>
                <div>{value}</div>
              </div>
            ))}
          </div>
        }
        items={[]}
        classes={{ panel: 'z-10' }}
      />
      <div
        className={classNames('mt-4', {
          'whitespace-break-spaces': mode === MessageViewMode.AIExtracted,
        })}
      >
        {mode === MessageViewMode.AIExtracted && (ai_extracted?.replaceAll('\n\n', '\n') || '---')}
        {mode === MessageViewMode.Original && (
          <div
            className="max-w-[512px] overflow-auto"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          ></div>
        )}
      </div>
      <SelectButtonsNew
        classes={{
          container: 'mt-2 w-fit',
          button: 'flex gap-1 justify-center !min-h-5 !h-5 !w-[108px] text-xs',
          buttonsContainer: '!min-h-6 !h-6',
        }}
        options={viewModeOptions}
        value={mode}
        onChange={setMode}
      />
      {!!documents?.length && (
        <div
          className={classNames('flex relative py-2 mt-4 gap-2 border-b', {
            'cursor-pointer': documents.length > 1,
            'flex-col': isDocumentsListOpen,
          })}
          onClick={documents.length > 1 ? handleToggleOpenReportsList : undefined}
        >
          {!isDocumentsListOpen && (
            <a
              href={getCorrespondenceDocumentFileUrl(documents[0])}
              target="_blank"
              rel="noopener noreferrer"
              className="w-fit"
            >
              <Badge className="flex gap-1 w-fit !text-gray-500 max-w-[90%]">
                <img src={ClipIcon} alt="file" />
                <span
                  className="text-wrap break-words overflow-hidden"
                  style={{ wordBreak: 'break-word' }}
                >
                  {getCorrespondenceDocumentFilename(documents[0])}
                </span>
              </Badge>
            </a>
          )}

          {!isDocumentsListOpen && documents.length > 1 && <Badge>+ {documents.length - 1}</Badge>}

          {isDocumentsListOpen &&
            documents.map((document, index) => (
              <a
                href={getCorrespondenceDocumentFileUrl(document)}
                target="_blank"
                rel="noopener noreferrer"
                className="w-fit"
              >
                <Badge className="flex gap-1 w-fit !text-gray-500 max-w-[90%]" key={index}>
                  <img src={ClipIcon} alt="file" />
                  <span className="break-words overflow-hidden" style={{ wordBreak: 'break-word' }}>
                    {getCorrespondenceDocumentFilename(document)}
                  </span>
                </Badge>
              </a>
            ))}

          {documents?.length > 1 && (
            <img
              src={ArrowShortLeftIcon}
              alt="disclosure"
              className={classNames('absolute top-5 right-1', {
                'rotate-90': isDocumentsListOpen,
                '-rotate-90': !isDocumentsListOpen,
              })}
            />
          )}
        </div>
      )}
    </div>
  );
};

interface IssuerCorrespondenceItemModalProps {
  item: CorrespondenceCommunicationActivityItem;
  open: boolean;
  onClose: () => void;
}

const IssuerCorrespondenceItemModal = ({
  item,
  open,
  onClose,
}: IssuerCorrespondenceItemModalProps) => {
  const modalScrollRef = useRef<HTMLDivElement>(null);

  const {
    subject,
    reports,
    text,
    type,
    ai_extracted,
    sender_email,
    sender_name,
    created_at,
    recipients,
    has_more,
    thread_root_id,
  } = item;
  const { data, isFetching, hasNextPage, fetchNextPage } = useIssuerCommunicationMessagesQuery(
    { root_id: thread_root_id || 0, page_size: 50, page: 1 },
    !thread_root_id,
  );
  const messages = (data?.pages || []).flatMap(({ data }) => data);

  useEffect(() => {
    if (has_more && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, has_more, isFetching]);

  useEffect(() => {
    if (modalScrollRef.current) {
      modalScrollRef.current.scrollTop = modalScrollRef.current.scrollHeight;
    }
  }, [modalScrollRef.current]);

  return (
    <ModalContentWrapper
      classes={{ dialogPanel: 'max-w-[560px]', children: 'max-w-[inherit]' }}
      variant="right"
      title={subject}
      onClose={onClose}
      open={open}
      modalScrollRef={modalScrollRef}
    >
      {isFetching && (
        <div className="h-72 flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      {!!thread_root_id && !!messages.length && (
        <div className="space-y-4">
          {messages.map(
            ({
              to_holders,
              cc_holders,
              date,
              subject,
              content,
              documents,
              message_id,
              sender_name,
              sender_email,
              ai_extracted,
            }) => {
              const resultSenderName =
                !sender_name && sender_email === 'communications@complione.com.au'
                  ? 'Compli One'
                  : sender_name;
              return (
                <CorrespondenceMessage
                  documents={documents}
                  key={message_id}
                  fromEmail={`<${sender_email}>`}
                  fromName={resultSenderName}
                  content={content}
                  ai_extracted={ai_extracted}
                  to={
                    to_holders
                      ? to_holders.map(({ recipient_email }) => recipient_email).join(', ')
                      : '---'
                  }
                  cc={
                    cc_holders
                      ? cc_holders.map(({ recipient_email }) => recipient_email).join(', ')
                      : '---'
                  }
                  date={date}
                  subject={subject || '---'}
                />
              );
            },
          )}
        </div>
      )}

      {!thread_root_id && (
        <CorrespondenceMessage
          fromName={
            <>
              {type === CommunicationType.External && <>{sender_name}</>}
              {type === CommunicationType.Communication && <>Compli One</>}
            </>
          }
          fromEmail={
            <>
              {type === CommunicationType.External && <>{`<${sender_email}>`}</>}
              {type === CommunicationType.Communication && <>{`<noreply@complione.com.au>`}</>}
            </>
          }
          documents={(reports || []).map(report => ({ report, is_report: true }))}
          content={text}
          ai_extracted={ai_extracted}
          to={recipients && recipients.map(({ recipient_email }) => recipient_email).join(', ')}
          date={created_at}
          subject={subject || '---'}
        />
      )}
    </ModalContentWrapper>
  );
};

export default IssuerCorrespondenceItemModal;
